import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { APP_CONFIG } from '../app.config.token';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface SeoData {
  title?: string;
  description?: string;
  image?: string;
  robots?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private appConfig = inject(APP_CONFIG);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        takeUntilDestroyed(),
      )
      .subscribe((data) => {
        if (data && (data['title'] || data['description'])) {
          this.updateTags({
            title: data['title'],
            description: data['description'],
            image: data['image'],
            robots: data['robots'],
          });
        }
      });
  }

  /**
   * Manually update tags. Useful for dynamic pages (e.g. Playlist details)
   * where the title depends on data fetched from the API.
   */
  updateTags(data: SeoData): void {
    const appName = environment.appName;

    if (data.title) {
      const fullTitle = `${data.title} | ${appName}`;
      this.titleService.setTitle(fullTitle);
      this.metaService.updateTag({ property: 'og:title', content: fullTitle });
      this.metaService.updateTag({
        property: 'twitter:title',
        content: fullTitle,
      });
    } else {
      this.titleService.setTitle(appName);
      this.metaService.updateTag({ property: 'og:title', content: appName });
      this.metaService.updateTag({
        property: 'twitter:title',
        content: appName,
      });
    }

    if (data.description) {
      this.metaService.updateTag({
        name: 'description',
        content: data.description,
      });
      this.metaService.updateTag({
        property: 'og:description',
        content: data.description,
      });
      this.metaService.updateTag({
        property: 'twitter:description',
        content: data.description,
      });
    }

    if (data.image) {
      this.metaService.updateTag({ property: 'og:image', content: data.image });
      this.metaService.updateTag({
        property: 'twitter:image',
        content: data.image,
      });
    } else {
      const baseUrl = this.appConfig.apiUrl.replace('/api', '');
      const defaultImage = `${baseUrl}/assets/logo.svg`;
      this.metaService.updateTag({
        property: 'og:image',
        content: defaultImage,
      });
      this.metaService.updateTag({
        property: 'twitter:image',
        content: defaultImage,
      });
    }

    if (data.robots) {
      this.metaService.updateTag({ name: 'robots', content: data.robots });
    } else {
      this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    }
  }
}
